import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import API_BASE_URL from "./config";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./Invoice.css";

const CURRENCY_SYMBOLS = {
  INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", AUD: "A$", CAD: "C$", SGD: "S$",
};

function Invoice() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const invoiceRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [toast, setToast] = useState(null);

  const [event, setEvent] = useState(null);
  const [studio, setStudio] = useState(null);
  const [invoice, setInvoice] = useState({
    invoice_number: "",
    bride_name: "",
    groom_name: "",
    event_date: "",
    items: [{ description: "Photography Services", amount: 0 }],
    total_amount: 0,
    advance_amount: 0,
    balance_amount: 0,
    notes: "",
  });
  const [isExisting, setIsExisting] = useState(false);

  const userId = localStorage.getItem("userId");

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    try {
      const [eventRes, userRes, invoiceRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/events/${userId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${API_BASE_URL}/api/user/${userId}`),
        fetch(`${API_BASE_URL}/api/invoices/event/${eventId}`),
      ]);

      if (!eventRes.ok) { navigate("/events"); return; }
      const eventsData = await eventRes.json();
      const eventData = eventsData.events?.find((e) => e.id === parseInt(eventId));
      if (!eventData) { navigate("/events"); return; }
      setEvent(eventData);

      if (userRes.ok) {
        const userData = await userRes.json();
        setStudio(userData.user || userData);
      }

      if (invoiceRes.ok) {
        const inv = await invoiceRes.json();
        setInvoice({
          invoice_number: inv.invoice_number || "",
          bride_name: inv.bride_name || "",
          groom_name: inv.groom_name || "",
          event_date: inv.event_date ? inv.event_date.split("T")[0] : "",
          items: inv.items || [{ description: "Photography Services", amount: 0 }],
          total_amount: parseFloat(inv.total_amount) || 0,
          advance_amount: parseFloat(inv.advance_amount) || 0,
          balance_amount: parseFloat(inv.balance_amount) || 0,
          notes: inv.notes || "",
        });
        setIsExisting(true);
      } else {
        // New invoice — get next number and pre-fill from event
        const numRes = await fetch(`${API_BASE_URL}/api/invoices/next-number/${userId}`);
        const numData = await numRes.json();
        setInvoice((prev) => ({
          ...prev,
          invoice_number: numData.invoice_number || "",
          bride_name: eventData.bridal_name || "",
          groom_name: eventData.groom_name || "",
          event_date: eventData.event_date ? eventData.event_date.split("T")[0] : "",
        }));
      }
    } catch (err) {
      showToast("Failed to load data", true);
    } finally {
      setLoading(false);
    }
  }, [eventId, userId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const recalculate = (items, advance) => {
    const total = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const adv = parseFloat(advance) || 0;
    return { total_amount: total, advance_amount: adv, balance_amount: total - adv };
  };

  const updateItem = (index, field, value) => {
    const newItems = [...invoice.items];
    newItems[index] = { ...newItems[index], [field]: field === "amount" ? parseFloat(value) || 0 : value };
    const calc = recalculate(newItems, invoice.advance_amount);
    setInvoice({ ...invoice, items: newItems, ...calc });
  };

  const addItem = () => {
    setInvoice({ ...invoice, items: [...invoice.items, { description: "", amount: 0 }] });
  };

  const removeItem = (index) => {
    if (invoice.items.length <= 1) return;
    const newItems = invoice.items.filter((_, i) => i !== index);
    const calc = recalculate(newItems, invoice.advance_amount);
    setInvoice({ ...invoice, items: newItems, ...calc });
  };

  const updateAdvance = (value) => {
    const adv = parseFloat(value) || 0;
    const calc = recalculate(invoice.items, adv);
    setInvoice({ ...invoice, ...calc });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/invoices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: parseInt(eventId),
          user_id: parseInt(userId),
          ...invoice,
        }),
      });
      if (!res.ok) throw new Error();
      setIsExisting(true);
      showToast("Invoice saved!");
    } catch {
      showToast("Failed to save invoice", true);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!invoiceRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2, useCORS: true, allowTaint: true, backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight <= pageHeight - 20) {
        pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      } else {
        // Multi-page
        let y = 0;
        const sliceHeight = Math.floor((canvas.width * (pageHeight - 20)) / imgWidth);
        while (y < canvas.height) {
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.min(sliceHeight, canvas.height - y);
          const ctx = sliceCanvas.getContext("2d");
          ctx.drawImage(canvas, 0, y, canvas.width, sliceCanvas.height, 0, 0, canvas.width, sliceCanvas.height);
          const sliceImg = sliceCanvas.toDataURL("image/png");
          const sliceImgHeight = (sliceCanvas.height * imgWidth) / canvas.width;
          if (y > 0) pdf.addPage();
          pdf.addImage(sliceImg, "PNG", 10, 10, imgWidth, sliceImgHeight);
          y += sliceHeight;
        }
      }

      const bride = (invoice.bride_name || "bride").replace(/\s+/g, "_").toLowerCase();
      const groom = (invoice.groom_name || "groom").replace(/\s+/g, "_").toLowerCase();
      pdf.save(`${bride}_${groom}_invoice.pdf`);
      showToast("Invoice downloaded!");
    } catch {
      showToast("Failed to download", true);
    } finally {
      setDownloading(false);
    }
  };

  const curr = CURRENCY_SYMBOLS[studio?.currency || "INR"] || "₹";

  if (loading) {
    return (
      <div className="inv-container">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="inv-main-content"><div className="inv-loading">Loading invoice...</div></div>
      </div>
    );
  }

  return (
    <div className="inv-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {toast && <div className={`inv-toast${toast.isError ? " inv-toast--error" : ""}`}>{toast.msg}</div>}

      <div className="inv-main-content">
        <header className="inv-header">
          <div className="inv-header-left">
            <button className="inv-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h1 className="inv-page-title">Invoice</h1>
          </div>
          <div className="inv-header-right">
            <button className="inv-btn inv-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : isExisting ? "Update Invoice" : "Save Invoice"}
            </button>
            <button className="inv-btn inv-btn--download" onClick={handleDownload} disabled={downloading}>
              {downloading ? "Generating..." : "⬇ Download PDF"}
            </button>
            <button className="inv-btn inv-btn--outline" onClick={() => navigate(`/events/${eventId}`)}>
              ← Back to Event
            </button>
          </div>
        </header>

        <main className="inv-main">
          {/* Editable Form */}
          <div className="inv-editor">
            <div className="inv-editor-section">
              <h3 className="inv-editor-title">Invoice Details</h3>
              <div className="inv-editor-grid">
                <div className="inv-field">
                  <label>Invoice Number</label>
                  <input value={invoice.invoice_number} readOnly className="inv-readonly" />
                </div>
                <div className="inv-field">
                  <label>Event Date</label>
                  <input type="date" value={invoice.event_date}
                    onChange={(e) => setInvoice({ ...invoice, event_date: e.target.value })} />
                </div>
                <div className="inv-field">
                  <label>Bride Name</label>
                  <input value={invoice.bride_name}
                    onChange={(e) => setInvoice({ ...invoice, bride_name: e.target.value })} />
                </div>
                <div className="inv-field">
                  <label>Groom Name</label>
                  <input value={invoice.groom_name}
                    onChange={(e) => setInvoice({ ...invoice, groom_name: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="inv-editor-section">
              <div className="inv-editor-section-header">
                <h3 className="inv-editor-title">Line Items</h3>
                <button className="inv-btn inv-btn--sm" onClick={addItem}>+ Add Item</button>
              </div>
              {invoice.items.map((item, i) => (
                <div key={i} className="inv-item-row">
                  <input className="inv-item-desc" placeholder="Description" value={item.description}
                    onChange={(e) => updateItem(i, "description", e.target.value)} />
                  <input className="inv-item-amt" type="number" placeholder="Amount" value={item.amount || ""}
                    onChange={(e) => updateItem(i, "amount", e.target.value)} />
                  {invoice.items.length > 1 && (
                    <button className="inv-item-remove" onClick={() => removeItem(i)}>✕</button>
                  )}
                </div>
              ))}
            </div>

            <div className="inv-editor-section">
              <div className="inv-editor-grid">
                <div className="inv-field">
                  <label>Total Amount</label>
                  <input value={`${curr} ${invoice.total_amount.toFixed(2)}`} readOnly className="inv-readonly inv-amount-total" />
                </div>
                <div className="inv-field">
                  <label>Advance Paid</label>
                  <input type="number" value={invoice.advance_amount || ""}
                    onChange={(e) => updateAdvance(e.target.value)} />
                </div>
                <div className="inv-field">
                  <label>Balance Due</label>
                  <input value={`${curr} ${invoice.balance_amount.toFixed(2)}`} readOnly className="inv-readonly inv-amount-balance" />
                </div>
              </div>
            </div>

            <div className="inv-editor-section">
              <div className="inv-field full">
                <label>Notes</label>
                <textarea value={invoice.notes} rows={3}
                  onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })}
                  placeholder="Additional notes, terms & conditions..." />
              </div>
            </div>
          </div>

          {/* Invoice Preview (for PDF download) */}
          <div className="inv-preview-wrap">
            <h3 className="inv-preview-label">Preview</h3>
            <div className="inv-preview" ref={invoiceRef}>
              {/* Header */}
              <div className="inv-p-header">
                <div className="inv-p-brand">
                  {studio?.logo_key && (
                    <img src={`${API_BASE_URL}/api/user/${userId}/logo`} alt="Studio Logo" className="inv-p-logo" crossOrigin="anonymous" />
                  )}
                  <div>
                    <h2 className="inv-p-studio">{studio?.studio_name || "Studio"}</h2>
                    {studio?.phone_number && <p className="inv-p-contact">📞 {studio.phone_number}</p>}
                    {studio?.email && <p className="inv-p-contact">✉ {studio.email}</p>}
                    {(studio?.address || studio?.city) && (
                      <p className="inv-p-contact">
                        📍 {[studio.address, studio.city, studio.state, studio.country].filter(Boolean).join(", ")}
                      </p>
                    )}
                    {studio?.gstin && <p className="inv-p-gstin">GSTIN: {studio.gstin}</p>}
                  </div>
                </div>
                <div className="inv-p-invoice-info">
                  <h1 className="inv-p-title">INVOICE</h1>
                  <p className="inv-p-num">{invoice.invoice_number}</p>
                  <p className="inv-p-date">Date: {invoice.event_date ? new Date(invoice.event_date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</p>
                </div>
              </div>

              <div className="inv-p-divider" />

              {/* Client Info */}
              <div className="inv-p-client">
                <h4 className="inv-p-section-label">Bill To</h4>
                <p className="inv-p-client-name">
                  {[invoice.bride_name, invoice.groom_name].filter(Boolean).join(" & ")}
                </p>
                <p className="inv-p-client-event">Event: {event?.event_name || "—"}</p>
              </div>

              {/* Items Table */}
              <table className="inv-p-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th className="inv-p-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{item.description || "—"}</td>
                      <td className="inv-p-right">{curr} {(parseFloat(item.amount) || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="inv-p-totals">
                <div className="inv-p-total-row">
                  <span>Subtotal</span>
                  <span>{curr} {invoice.total_amount.toFixed(2)}</span>
                </div>
                <div className="inv-p-total-row">
                  <span>Advance Paid</span>
                  <span>- {curr} {invoice.advance_amount.toFixed(2)}</span>
                </div>
                <div className="inv-p-total-row inv-p-total-final">
                  <span>Balance Due</span>
                  <span>{curr} {invoice.balance_amount.toFixed(2)}</span>
                </div>
              </div>

              {/* Notes */}
              {invoice.notes && (
                <div className="inv-p-notes">
                  <h4 className="inv-p-section-label">Notes</h4>
                  <p>{invoice.notes}</p>
                </div>
              )}

              {/* Footer */}
              <div className="inv-p-footer">
                <p>Thank you for choosing {studio?.studio_name || "our studio"}!</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Invoice;
